import React from 'react';
import {
  Box,
  Heading,
  Text,
  Badge,
  Flex,
  Code,
  Divider,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';

const methodColors = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  DELETE: 'red',
};

const ApiDocs = () => {
  const instanceEndpoints = [
    { 
      title: 'List all instances', 
      method: 'GET', 
      path: '/api/v1/instances/', 
      description: 'Retrieve a list of all registered instances.',
      response: `[
  {
    "id": 1,
    "name": "Production Instance",
    "host": "app.example.com",
    "instance_type": "production",
    "max_users": 100,
    "max_events": 50,
    "is_active": true
  }
]`
    },
    { 
      title: 'Get instance details', 
      method: 'GET', 
      path: '/api/v1/instances/:id/', 
      description: 'Retrieve details for a specific instance by ID.',
      response: `{
  "id": 1,
  "name": "Production Instance",
  "host": "app.example.com",
  "instance_type": "production",
  "max_users": 100,
  "max_events": 50,
  "is_active": true
}`
    },
    { 
      title: 'Create instance', 
      method: 'POST', 
      path: '/api/v1/instances/', 
      description: 'Create a new instance with the provided data.',
      request: `{
  "name": "New Instance",
  "host": "new.example.com",
  "instance_type": "staging",
  "max_users": 50,
  "max_events": 25,
  "is_active": true
}`,
      response: `{
  "id": 2,
  "name": "New Instance",
  "host": "new.example.com",
  "instance_type": "staging",
  "max_users": 50,
  "max_events": 25,
  "is_active": true
}`
    },
    { 
      title: 'Update instance', 
      method: 'PUT', 
      path: '/api/v1/instances/:id/', 
      description: 'Update an existing instance by ID.',
      request: `{
  "name": "Updated Instance",
  "host": "updated.example.com",
  "instance_type": "production",
  "max_users": 200,
  "max_events": 100,
  "is_active": true
}`,
      response: `{
  "id": 1,
  "name": "Updated Instance",
  "host": "updated.example.com",
  "instance_type": "production",
  "max_users": 200,
  "max_events": 100,
  "is_active": true
}`
    },
    { 
      title: 'Delete instance', 
      method: 'DELETE', 
      path: '/api/v1/instances/:id/', 
      description: 'Permanently delete an instance by ID.',
      response: `204 No Content`
    },
  ];

  const wordpressEndpoints = [
    { 
      title: 'List configurations', 
      method: 'GET', 
      path: '/api/v1/wordpress/', 
      description: 'Retrieve all WordPress configurations.',
      response: `[
  {
    "id": 1,
    "name": "Production WP",
    "host": "https://wordpress.example.com",
    "username": "api_user",
    "verify_ssl": true,
    "is_active": true
  }
]`
    },
    { 
      title: 'Get configuration', 
      method: 'GET', 
      path: '/api/v1/wordpress/:id/', 
      description: 'Retrieve a specific WordPress configuration.',
      response: `{
  "id": 1,
  "name": "Production WP",
  "host": "https://wordpress.example.com",
  "username": "api_user",
  "verify_ssl": true,
  "is_active": true
}`
    },
    { 
      title: 'Create configuration', 
      method: 'POST', 
      path: '/api/v1/wordpress/', 
      description: 'Create a new WordPress configuration.',
      request: `{
  "name": "New WP Site",
  "host": "https://newsite.example.com",
  "username": "admin",
  "password": "secure_password",
  "webhook_secret": "your_secret_key",
  "verify_ssl": true,
  "is_active": true
}`,
      response: `{
  "id": 2,
  "name": "New WP Site",
  "host": "https://newsite.example.com",
  "username": "admin",
  "verify_ssl": true,
  "is_active": true
}`
    },
    { 
      title: 'Update configuration', 
      method: 'PUT', 
      path: '/api/v1/wordpress/:id/', 
      description: 'Update an existing WordPress configuration.',
      request: `{
  "name": "Updated WP Site",
  "host": "https://updated.example.com",
  "username": "new_admin",
  "password": "new_password",
  "webhook_secret": "new_secret",
  "verify_ssl": false,
  "is_active": true
}`,
      response: `{
  "id": 1,
  "name": "Updated WP Site",
  "host": "https://updated.example.com",
  "username": "new_admin",
  "verify_ssl": false,
  "is_active": true
}`
    },
    { 
      title: 'Delete configuration', 
      method: 'DELETE', 
      path: '/api/v1/wordpress/:id/', 
      description: 'Delete a WordPress configuration.',
      response: `204 No Content`
    },
  ];

  const webhookEndpoints = [
    { 
      title: 'List webhooks', 
      method: 'GET', 
      path: '/api/v1/webhook-configs/', 
      description: 'Retrieve all configured webhooks.',
      response: `[
  {
    "id": 1,
    "name": "Slack Notifications",
    "url": "https://hooks.slack.com/...",
    "events": "all",
    "is_active": true,
    "verify_ssl": true,
    "created_at": "2026-01-15T10:00:00Z",
    "recent_deliveries": []
  }
]`
    },
    { 
      title: 'Get webhook', 
      method: 'GET', 
      path: '/api/v1/webhook-configs/:id/', 
      description: 'Retrieve a specific webhook configuration.',
      response: `{
  "id": 1,
  "name": "Slack Notifications",
  "url": "https://hooks.slack.com/...",
  "events": "all",
  "is_active": true,
  "verify_ssl": true,
  "created_at": "2026-01-15T10:00:00Z",
  "recent_deliveries": []
}`
    },
    { 
      title: 'Create webhook', 
      method: 'POST', 
      path: '/api/v1/webhook-configs/', 
      description: 'Create a new webhook configuration.',
      request: `{
  "name": "My Webhook",
  "url": "https://example.com/webhook",
  "secret": "optional_secret_for_signing",
  "events": "instance.created,instance.updated",
  "is_active": true,
  "verify_ssl": true
}`,
      response: `{
  "id": 2,
  "name": "My Webhook",
  "url": "https://example.com/webhook",
  "events": "instance.created,instance.updated",
  "is_active": true,
  "verify_ssl": true,
  "created_at": "2026-01-17T14:30:00Z"
}`
    },
    { 
      title: 'Update webhook', 
      method: 'PUT', 
      path: '/api/v1/webhook-configs/:id/', 
      description: 'Update an existing webhook.',
      request: `{
  "name": "Updated Webhook",
  "url": "https://example.com/new-endpoint",
  "events": "all",
  "is_active": false
}`,
      response: `{
  "id": 1,
  "name": "Updated Webhook",
  "url": "https://example.com/new-endpoint",
  "events": "all",
  "is_active": false,
  "verify_ssl": true
}`
    },
    { 
      title: 'Delete webhook', 
      method: 'DELETE', 
      path: '/api/v1/webhook-configs/:id/', 
      description: 'Delete a webhook configuration.',
      response: `204 No Content`
    },
    { 
      title: 'Test webhook', 
      method: 'POST', 
      path: '/api/v1/webhook-configs/:id/test/', 
      description: 'Send a test payload to verify the webhook is working.',
      response: `{
  "success": true,
  "delivery_id": 123,
  "status": "success",
  "response_status": 200,
  "response_body": "OK"
}`
    },
    { 
      title: 'Get delivery history', 
      method: 'GET', 
      path: '/api/v1/webhook-configs/:id/deliveries/', 
      description: 'Get the delivery history for a webhook.',
      response: `[
  {
    "id": 1,
    "event": "instance.created",
    "payload": {"event": "instance.created", "data": {...}},
    "status": "success",
    "response_status": 200,
    "created_at": "2026-01-17T12:00:00Z"
  }
]`
    },
    { 
      title: 'Verify signature', 
      method: 'POST', 
      path: '/api/v1/webhook-configs/verify_signature/', 
      description: 'Verify that a webhook signature is valid.',
      request: `{
  "payload": {"event": "test", "data": {}},
  "secret": "your_webhook_secret",
  "signature": "sha256=abc123..."
}`,
      response: `{
  "valid": true
}`
    },
  ];

  const EndpointCard = ({ endpoint }) => (
    <Box
      p={5}
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.100"
      transition="all 0.2s"
      _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
    >
      <Flex align="center" mb={3}>
        <Badge
          colorScheme={methodColors[endpoint.method]}
          px={3}
          py={1}
          borderRadius="full"
          fontWeight="bold"
          fontSize="xs"
        >
          {endpoint.method}
        </Badge>
        <Text ml={3} fontWeight="semibold" fontSize="md" color="gray.700">
          {endpoint.title}
        </Text>
      </Flex>
      <Code
        display="block"
        p={3}
        borderRadius="md"
        bg="gray.50"
        fontSize="sm"
        color="purple.600"
        fontWeight="medium"
      >
        {endpoint.path}
      </Code>
      <Text mt={3} fontSize="sm" color="gray.500">
        {endpoint.description}
      </Text>
      
      {(endpoint.request || endpoint.response) && (
        <Accordion allowToggle mt={4}>
          {endpoint.request && (
            <AccordionItem border="none">
              <AccordionButton 
                px={3} 
                py={2} 
                bg="blue.50" 
                borderRadius="md"
                _hover={{ bg: 'blue.100' }}
              >
                <Box flex="1" textAlign="left">
                  <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                    Request Body
                  </Badge>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={2} pt={2}>
                <Code
                  display="block"
                  p={3}
                  borderRadius="md"
                  bg="gray.800"
                  color="green.300"
                  fontSize="xs"
                  whiteSpace="pre"
                  overflowX="auto"
                >
                  {endpoint.request}
                </Code>
              </AccordionPanel>
            </AccordionItem>
          )}
          {endpoint.response && (
            <AccordionItem border="none" mt={endpoint.request ? 2 : 0}>
              <AccordionButton 
                px={3} 
                py={2} 
                bg="green.50" 
                borderRadius="md"
                _hover={{ bg: 'green.100' }}
              >
                <Box flex="1" textAlign="left">
                  <Badge colorScheme="green" variant="subtle" fontSize="xs">
                    Response
                  </Badge>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={2} pt={2}>
                <Code
                  display="block"
                  p={3}
                  borderRadius="md"
                  bg="gray.800"
                  color="green.300"
                  fontSize="xs"
                  whiteSpace="pre"
                  overflowX="auto"
                >
                  {endpoint.response}
                </Code>
              </AccordionPanel>
            </AccordionItem>
          )}
        </Accordion>
      )}
    </Box>
  );

  return (
    <Box>
      <Box mb={8}>
        <Heading size="lg" color="gray.800" mb={2}>API Documentation</Heading>
        <Text color="gray.500">Explore the available API endpoints for managing instances, WordPress configurations, and webhooks.</Text>
      </Box>

      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Instances</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {instanceEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {instanceEndpoints.map((endpoint, index) => (
            <EndpointCard key={index} endpoint={endpoint} />
          ))}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">WordPress Configurations</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {wordpressEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {wordpressEndpoints.map((endpoint, index) => (
            <EndpointCard key={index} endpoint={endpoint} />
          ))}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Webhooks</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            {webhookEndpoints.length} endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {webhookEndpoints.map((endpoint, index) => (
            <EndpointCard key={index} endpoint={endpoint} />
          ))}
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">API Keys</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            5 endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          <EndpointCard endpoint={{
            title: 'List API keys',
            method: 'GET',
            path: '/api/v1/api-keys/',
            description: 'List all API keys.',
            response: `[
  {
    "id": 1,
    "name": "Production Key",
    "key": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "description": "Used for production API access",
    "is_active": true,
    "created_at": "2026-01-01T00:00:00Z",
    "updated_at": "2026-01-01T00:00:00Z"
  }
]`
          }} />
          <EndpointCard endpoint={{
            title: 'Get API key',
            method: 'GET',
            path: '/api/v1/api-keys/{id}/',
            description: 'Get details of a specific API key.',
            response: `{
  "id": 1,
  "name": "Production Key",
  "key": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "description": "Used for production API access",
  "is_active": true,
  "created_at": "2026-01-01T00:00:00Z",
  "updated_at": "2026-01-01T00:00:00Z"
}`
          }} />
          <EndpointCard endpoint={{
            title: 'Create API key',
            method: 'POST',
            path: '/api/v1/api-keys/',
            description: 'Create a new API key. The key value is auto-generated as a UUID.',
            request: `{
  "name": "Production Key",
  "description": "Used for production API access",
  "is_active": true
}`,
            response: `{
  "id": 1,
  "name": "Production Key",
  "key": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "description": "Used for production API access",
  "is_active": true,
  "created_at": "2026-01-01T00:00:00Z",
  "updated_at": "2026-01-01T00:00:00Z"
}`
          }} />
          <EndpointCard endpoint={{
            title: 'Update API key',
            method: 'PUT',
            path: '/api/v1/api-keys/{id}/',
            description: 'Update an existing API key. The key value cannot be changed.',
            request: `{
  "name": "Updated Key Name",
  "description": "Updated description",
  "is_active": false
}`,
            response: `{
  "id": 1,
  "name": "Updated Key Name",
  "key": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "description": "Updated description",
  "is_active": false,
  "created_at": "2026-01-01T00:00:00Z",
  "updated_at": "2026-01-15T12:00:00Z"
}`
          }} />
          <EndpointCard endpoint={{
            title: 'Delete API key',
            method: 'DELETE',
            path: '/api/v1/api-keys/{id}/',
            description: 'Delete an API key.',
            response: `204 No Content`
          }} />
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Metrics & Statistics</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            2 endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          <EndpointCard endpoint={{
            title: 'Report Metrics',
            method: 'POST',
            path: '/api/v1/report_metric',
            description: 'Report instance metrics. Authenticated via x-api-key header matching an instance API key. Supports single metric or bulk format.',
            request: `// Bulk format (preferred):
{
  "metrics": {
    "profiles": 42,
    "active_profiles": 38,
    "organisers": 5,
    "attendees": 33,
    "events": 12,
    "event_instances": 18,
    "registrations": 150,
    "emails_sent": 320,
    "courses": 4,
    "course_sessions": 10
  }
}

// Single metric format:
{
  "metric": "profiles",
  "value": 42
}`,
            response: `{
  "success": true,
  "saved": 10
}`
          }} />
          <EndpointCard endpoint={{
            title: 'Instance Statistics',
            method: 'GET',
            path: '/api/v1/statistics/?days=7&instance_id=',
            description: 'Get time-series metrics for all instances or a specific one. Granularity is hourly (≤3 days) or daily (>3 days). Max 90 days.',
            response: `{
  "period_days": 7,
  "granularity": "hour",
  "metric_names": ["profiles", "events", ...],
  "series": {
    "profiles": [
      {"time": "2026-02-10T14:00:00+00:00", "value": 42.0}
    ],
    "events": [...]
  },
  "snapshots": [
    {
      "instance_id": 1,
      "instance_name": "Production",
      "is_lite": false,
      "metrics": {"profiles": 42, "events": 12}
    }
  ]
}`
          }} />
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box mb={8}>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">Insights</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            2 endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          <EndpointCard endpoint={{
            title: 'Usage Analytics',
            method: 'GET',
            path: '/api/v1/analytics/?days=30',
            description: 'Get usage analytics over a configurable time period (max 365 days). Includes audit log activity, webhook deliveries, and instance type breakdowns.',
            response: `{
  "period_days": 30,
  "audit_by_day": [
    {"date": "2026-02-01", "count": 15}
  ],
  "audit_by_action": [
    {"action": "create", "count": 50}
  ],
  "webhooks_by_day": [
    {"date": "2026-02-01", "count": 10}
  ],
  "instance_types": [
    {"type": "full", "count": 8},
    {"type": "lite", "count": 3}
  ],
  "total_instances": 11
}`
          }} />
          <EndpointCard endpoint={{
            title: 'Health Check',
            method: 'GET',
            path: '/api/v1/health/',
            description: 'Check internal health of the License Manager service including database connectivity, instance stats, and webhook configuration.',
            response: `{
  "overall": "healthy",
  "checks": {
    "database": {
      "status": "healthy",
      "latency_ms": 1.23
    },
    "instances": {
      "status": "healthy",
      "total": 11,
      "full": 8,
      "lite": 3
    },
    "webhooks": {
      "status": "healthy",
      "total": 3,
      "active": 2
    }
  }
}`
          }} />
        </SimpleGrid>
      </Box>

      <Divider my={8} />

      <Box>
        <Flex align="center" mb={4}>
          <Heading size="md" color="gray.700">PostgreSQL Status</Heading>
          <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
            4 endpoints
          </Badge>
        </Flex>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          <EndpointCard endpoint={{
            title: 'Server Status',
            method: 'GET',
            path: '/api/v1/postgres/status/',
            description: 'Get server version, uptime, database size, connection stats and configuration.',
            response: '{"success": true, "postgres": {"connected": true, "server_info": {...}, "database": {...}, "connections": {...}}}'
          }} />
          <EndpointCard endpoint={{
            title: 'List Tables',
            method: 'GET',
            path: '/api/v1/postgres/tables/?page=1&sort=size&search=',
            description: 'List tables with sizes, row counts, and vacuum stats. Supports pagination, sorting, and search.',
            response: '{"success": true, "tables": [...], "pagination": {"page": 1, "pages": 1, "total": 10}}'
          }} />
          <EndpointCard endpoint={{
            title: 'Active Queries',
            method: 'GET',
            path: '/api/v1/postgres/activity/',
            description: 'Get active queries and connections from pg_stat_activity.',
            response: '{"success": true, "activity": [{"pid": 123, "state": "active", "query": "SELECT ..."}]}'
          }} />
          <EndpointCard endpoint={{
            title: 'Execute SQL Query',
            method: 'POST',
            path: '/api/v1/postgres/query/',
            description: 'Execute a read-only SQL query against the database. Only SELECT statements are allowed.',
            request: `{
  "sql": "SELECT * FROM manager_instance LIMIT 10"
}`,
            response: `{
  "success": true,
  "columns": ["id", "name", "host", "is_active"],
  "rows": [
    [1, "Production", "app.example.com", true]
  ],
  "row_count": 1
}`
          }} />
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default ApiDocs;
